/**
 * DropInSubRail — Kick-style creator navigation rail rendered alongside
 * the global DevourSidebar on /drop-in and individual Drop pages.
 *
 * Three sections, top-to-bottom:
 *   - Following — user's followed creators (offline rows are dimmed)
 *   - Live Now — globally live creators excluding followed
 *   - Recommended — recently-active / popular creators
 *
 * Each section is collapsible past `COLLAPSED_LIMIT` rows with a "Show
 * More / Show Less" toggle. Live rows show a green dot + viewer count on
 * the right; offline rows render dimmed with the game name as sub-line
 * (or no sub-line when no game context is known).
 *
 * Empty sections render a single-line placeholder rather than disappearing
 * so the rail's structure stays visible.
 *
 * Rail-level collapse: a toggle at the top folds the entire rail down to
 * a narrow icon-strip — section headers become icons, rows shrink to
 * just the avatar. The collapsed state persists in localStorage so it
 * survives navigation. Wide → narrow transitions smoothly via CSS width.
 */

import {ReactElement, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {FaHeart, FaVideo, FaStar, FaChevronLeft, FaChevronRight} from "react-icons/fa";
import {useGetDropInSubRail, type DropInSubRailItem} from "@/hooks/drop-in/useGetDropInSubRail";
import {useAllGameIcons} from "@/hooks/drop-in/useAllGameIcons";
import {resolveGameIcon} from "@/hooks/drop-in/resolveGameIcon";
import "./DropInSubRail.scss";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

const COLLAPSED_LIMIT = 5;
const COLLAPSE_PERSIST_KEY = "drop-in-sub-rail:collapsed";

interface DropInSubRailProps {
    /** When set, the sub-rail highlights the row whose handle matches. */
    activeHandle?: string;
}


function SubRailRow({item, isActive, onClick, collapsed}: {
    item: DropInSubRailItem;
    isActive: boolean;
    onClick: () => void;
    collapsed: boolean;
}): ReactElement {
    const cls = [
        "drop-in-sub-rail__row",
        item.isLive ? "drop-in-sub-rail__row--live" : "drop-in-sub-rail__row--offline",
        isActive ? "drop-in-sub-rail__row--active" : "",
    ].filter(Boolean).join(" ");

    /*
     * Resolve a real game icon for the gameName sub-line via the shared fuzzy
     * resolver (exact → base → alias → fuzzy), so stream categories match the
     * catalog even when the naming drifts. Falls back to text-only when unmatched.
     */
    const {data: gameIcons} = useAllGameIcons();
    const gameIcon = useMemo(() => resolveGameIcon(gameIcons, item.gameName), [item.gameName, gameIcons]);

    return (
        <button
            type="button"
            className={cls}
            onClick={onClick}
            title={item.gameName ? `${item.displayName} — ${item.gameName}` : item.displayName}
        >
            <span className="drop-in-sub-rail__avatar">
                {item.profileImageUrl
                    ? <img src={item.profileImageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
                    : <span className="drop-in-sub-rail__avatar-fallback">{item.displayName.charAt(0).toUpperCase()}</span>}
                {/*
                  * Collapsed state shows the live-dot directly on the avatar
                  * (bottom-right corner) since the row-level live-meta block
                  * is hidden. The full expanded state keeps the dot + viewer
                  * count side-by-side on the right of the row.
                  */}
                {collapsed && item.isLive &&
                    <span className="drop-in-sub-rail__avatar-live-dot" aria-hidden="true" />
                }
            </span>
            {!collapsed &&
                <>
                    <span className="drop-in-sub-rail__meta">
                        <span className="drop-in-sub-rail__name">{item.displayName}</span>
                        {item.gameName &&
                            <span className="drop-in-sub-rail__sub">
                                {gameIcon &&
                                    <img className="drop-in-sub-rail__game-icon" src={gameIcon} alt="" loading="lazy" referrerPolicy="no-referrer" />
                                }
                                {item.gameName}
                            </span>
                        }
                    </span>
                    {item.isLive && typeof item.viewerCount === "number" && item.viewerCount > 0 &&
                        <span className="drop-in-sub-rail__live-meta">
                            <span className="drop-in-sub-rail__live-dot" aria-hidden="true" />
                            <span>{formatCompactNumber(item.viewerCount)}</span>
                        </span>
                    }
                </>
            }
        </button>
    );
}

/**
 * One labeled section with a collapsing list of rows. When the list is
 * longer than COLLAPSED_LIMIT, only the first N rows render with a
 * "Show More" toggle below; expanded state shows everything with a
 * "Show Less" toggle. When the parent rail is in icon-only collapsed
 * mode, the section header is replaced with a single icon and the
 * empty-state placeholder + show-more toggle hide.
 */
function SubRailSection({label, icon, items, activeHandle, onGoToDrop, emptyText, collapsed, headerExtra}: {
    label: string;
    icon: ReactElement;
    items: DropInSubRailItem[];
    activeHandle?: string;
    onGoToDrop: (handle: string) => void;
    emptyText: string;
    collapsed: boolean;
    /**
     * Optional slot rendered to the right of the section header — used by
     * the first section to host the rail-level collapse toggle so it
     * aligns with the "Following" baseline (and therefore with the
     * Play icon on the global DevourPlaySidebar).
     */
    headerExtra?: ReactElement;
}): ReactElement {
    const [expanded, setExpanded] = useState(false);
    const visibleItems = expanded ? items : items.slice(0, COLLAPSED_LIMIT);
    const hasMore = items.length > COLLAPSED_LIMIT;

    return (
        <section className="drop-in-sub-rail__section">
            <div className="drop-in-sub-rail__header-row">
                {collapsed
                    ? <div className="drop-in-sub-rail__header-icon" title={label} aria-label={label}>{icon}</div>
                    : <h3 className="drop-in-sub-rail__header">{label}</h3>
                }
                {headerExtra}
            </div>
            {items.length > 0
                ? <>
                    {visibleItems.map(item =>
                        <SubRailRow
                            key={item.id}
                            item={item}
                            isActive={activeHandle === item.handle}
                            onClick={() => onGoToDrop(item.handle)}
                            collapsed={collapsed}
                        />)}
                    {hasMore && !collapsed &&
                        <button
                            type="button"
                            className="drop-in-sub-rail__show-more"
                            onClick={() => setExpanded(prev => !prev)}
                        >
                            {expanded ? "Show Less" : `Show More (${items.length - COLLAPSED_LIMIT})`}
                        </button>
                    }
                </>
                : !collapsed &&
                    <div className="drop-in-sub-rail__empty">{emptyText}</div>
            }
        </section>
    );
}

export function DropInSubRail({activeHandle}: DropInSubRailProps): ReactElement {
    const {data} = useGetDropInSubRail();
    const navigate = useNavigate();
    /*
     * Collapse state survives navigation via localStorage. Initial render
     * reads the persisted value synchronously so the rail doesn't flash
     * expanded-then-collapsed on subsequent loads.
     */
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        /*
         * Default-collapsed on first visit. Drop-In leads with the
         * trending grid; an expanded sub-rail on first paint pushes
         * content right and competes for attention before the user
         * has expressed an intent. localStorage still wins once the
         * user explicitly toggles either way, so a returning user
         * keeps their preferred state.
         *
         * Guarded against (a) SSR / non-window contexts and (b) sandboxed
         * iframes and "null origin" contexts where localStorage access
         * throws SecurityError. Falls back to collapsed in either case.
         */
        if (typeof window === "undefined") return true;
        try {
            const stored = window.localStorage.getItem(COLLAPSE_PERSIST_KEY);
            return stored === null ? true : stored === "1";
        } catch {
            return true;
        }
    });
    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            window.localStorage.setItem(COLLAPSE_PERSIST_KEY, collapsed ? "1" : "0");
        } catch { /* localStorage unavailable — preference lost for the session, no-op */ }
    }, [collapsed]);

    const goToDrop = (handle: string): void => {
        navigate(`/drop-in/${handle}`);
    };

    const following = data?.following ?? [];
    const recommended = data?.recommended ?? [];
    /*
     * Backend's `liveNow` is "globally live creators excluding the
     * viewer's follows" — clean separation server-side, but it reads as
     * a bug when a followed creator is the only one live and the section
     * shows "No one is live right now" despite the ticker saying "1 LIVE
     * NOW". Merge live followed creators into the displayed list so the
     * section reflects actual live state. De-duped by id.
     */
    const backendLiveNow = data?.liveNow ?? [];
    const followingLive = following.filter(f => f.isLive);
    const liveNowSeen = new Set(backendLiveNow.map(item => item.id));
    const liveNow = [
        ...backendLiveNow,
        ...followingLive.filter(item => !liveNowSeen.has(item.id)),
    ];

    const toggleButton =
        <button
            type="button"
            className="drop-in-sub-rail__toggle"
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? "Expand Drop-In rail" : "Collapse Drop-In rail"}
            title={collapsed ? "Expand" : "Collapse"}
        >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>;
    return (
        <aside className={`drop-in-sub-rail${collapsed ? " drop-in-sub-rail--collapsed" : ""}`} aria-label="Drop-In navigation">
            <SubRailSection
                label="Following"
                icon={<FaHeart />}
                items={following}
                activeHandle={activeHandle}
                onGoToDrop={goToDrop}
                emptyText="Follow creators to see them here"
                collapsed={collapsed}
                headerExtra={toggleButton}
            />
            <SubRailSection
                label="Live Now"
                icon={<FaVideo />}
                items={liveNow}
                activeHandle={activeHandle}
                onGoToDrop={goToDrop}
                emptyText="No one is live right now"
                collapsed={collapsed}
            />
            <SubRailSection
                label="Recommended"
                icon={<FaStar />}
                items={recommended}
                activeHandle={activeHandle}
                onGoToDrop={goToDrop}
                emptyText="Browse the directory to find creators"
                collapsed={collapsed}
            />
        </aside>
    );
}
